// The backend allows thirty years of part-time work between the participant and
// the spouse, so each of them gets fifteen. Both the starting and the stopping
// age are worked, so fifteen years of work stops fourteen years after it starts
// -- the off-by-one that let multi-year goals run eleven years (GC-21552).
export const MAX_PART_TIME_WORK_YEARS = 15

export const OLDEST_PART_TIME_WORK_AGE = 100

export const PART_TIME_WORK_SPAN_MESSAGE = `Part-time work during retirement is limited to ${MAX_PART_TIME_WORK_YEARS} years`

/**
 * @desc Last age the part-time work stopping-age dropdown may offer. The floor
 * of that dropdown is unchanged (it still comes from the retirement age); this
 * only pulls the ceiling in to fifteen years of work.
 *
 * The cap holds even for a record saved before it existed. Such a record opens
 * with a stopping age outside the dropdown, which then shows that age as its
 * placeholder rather than as a selection -- and the form's own validation
 * refuses the span until a new stopping age is chosen.
 * @param startingAge the starting age currently chosen, null until one is
 * @return Number
 */
export const maxPartTimeStoppingAge = (startingAge, oldestAge = OLDEST_PART_TIME_WORK_AGE) => {
  if (startingAge === null || startingAge === undefined) {
    return oldestAge
  }

  return Math.min(oldestAge, startingAge + MAX_PART_TIME_WORK_YEARS - 1)
}

/**
 * @desc First age the part-time work stopping-age dropdown may offer. Work
 * cannot stop before it starts, so once a starting age is chosen the list
 * begins there rather than at the retirement age, and the dropdown stops
 * offering ages the not-before-the-starting-age rule would reject.
 * @param startingAge the starting age currently chosen, null until one is
 * @param retirementAge the floor the dropdown had before a starting age was
 * chosen -- kept as a floor of its own, since part-time work in retirement
 * cannot start before the retirement age either
 * @return Number
 */
export const minPartTimeStoppingAge = (startingAge, retirementAge) => {
  if (startingAge === null || startingAge === undefined) {
    return retirementAge
  }

  return Math.max(startingAge, retirementAge)
}

/**
 * @desc Whether a chosen span is within the fifteen-year cap. Shares its ceiling
 * with the dropdown, so the form can never offer an age its validation refuses.
 * An unanswered age is not this rule's to reject -- the required and
 * not-before-the-starting-age rules already cover those.
 * @return Boolean
 */
export const isPartTimeSpanWithinCap = (startingAge, stoppingAge) => {
  if (
    startingAge === null ||
    startingAge === undefined ||
    stoppingAge === null ||
    stoppingAge === undefined
  ) {
    return true
  }

  return stoppingAge <= maxPartTimeStoppingAge(startingAge)
}

/**
 * @desc final-form-calculate update for the stopping age when the starting age
 * changes. Changing the starting age re-bases the fifteen-year cap, so a
 * stopping age that no longer fits comes back to the last age the new range
 * allows.
 * @return Number | null
 */
export const clampPartTimeStoppingAge = (startingAge, allValues, previousValues) => {
  const stoppingAge = allValues.durationStoppingAge

  // Runs once on mount with no previous values. A stored over-long span is left
  // as it is there: the participant is shown the error and picks a new stopping
  // age themselves, rather than having the form quietly shorten a span they may
  // be counting on.
  if (!previousValues) {
    return stoppingAge
  }

  if (
    startingAge === null ||
    startingAge === undefined ||
    stoppingAge === null ||
    stoppingAge === undefined
  ) {
    return stoppingAge
  }

  return Math.min(stoppingAge, maxPartTimeStoppingAge(startingAge))
}
